import { Box, ColorSwatch, Flex, Grid, Paper, RingProgress, SegmentedControl, Text } from "@mantine/core"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { IByPie, IDashboard } from "../../../models/Dashboard"
import { IGeneral } from "../../../store/General"
import { tugrug } from "../../../utils"
import classes from "../real.module.css"
import { CardHeader } from "./CardHeader"

interface Props {
  byPie: IDashboard["byPie"] | undefined
  isBuyer: boolean
  filter: string | null
  onFilterChange: (next: string | null) => void
}

export function PartnerBreakdownCard({ byPie, isBuyer, filter, onFilterChange }: Props) {
  const { t } = useTranslation(["dashboard", "common"])
  const dashboardPieFilters = useSelector(
    (state: { general: IGeneral }) => state.general.dashboardPieFilters,
  )

  const pie = useMemo(() => byPie ?? [], [byPie])
  const pieTotal = pie.reduce((sum, item) => sum + (item?.amount || 0), 0)

  const options = useMemo(
    () => buildPieOptions(pie, t("dashboard:chart.orderAmount"), `${t("dashboard:total")}<br/><b>${formatTotal(pieTotal, t("dashboard:numberFormat.million"))}</b>`),
    [pie, pieTotal, t],
  )

  return (
    <Paper className={classes.card}>
      <CardHeader
        title={t(isBuyer ? "dashboard:widgets.supplierBreakdown" : "dashboard:widgets.partnerBreakdown")}
        subtitle={t("dashboard:partnerSub")}
        right={
          <SegmentedControl
            value={filter ?? ""}
            onChange={onFilterChange}
            data={(dashboardPieFilters || []).map((i) => ({
              label: t(`dashboard:pieFilter.${i?.code}`, { defaultValue: i?.name }),
              value: i?.code,
            }))}
          />
        }
      />

      <Grid gap="md" align="center">
        <Grid.Col span={{ base: 12, sm: 5 }}>
          {pieTotal > 0 ? (
            <HighchartsReact highcharts={Highcharts} options={options} />
          ) : (
            <Flex justify="center">
              <RingProgress
                size={180}
                thickness={20}
                sections={[{ value: 100, color: "gray.2" }]}
                label={<Text ta="center" c="dimmed" fz={14}>-</Text>}
              />
            </Flex>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 7 }}>
          <Box style={{ maxHeight: 260, overflowY: "auto" }}>
            {pie.length === 0 ? (
              <Text fz={14} c="dimmed" ta="center" py="md">-</Text>
            ) : (
              pie.map((c: IByPie, index: number) => (
                <Box key={index} className={classes.sRow}>
                  <Flex align="center" gap="sm">
                    <ColorSwatch color={c.color} size={10} />
                    <Box>
                      <Text className={classes.partnerName} lineClamp={1}>{c?.name || "-"}</Text>
                      <Text className={classes.partnerMeta}>
                        {c?.ordersCount} {t("common:table.order")}
                      </Text>
                    </Box>
                  </Flex>
                  <Text className={classes.partnerAmount}>{tugrug(c?.amount || 0)}</Text>
                </Box>
              ))
            )}
          </Box>
        </Grid.Col>
      </Grid>
    </Paper>
  )
}

function formatTotal(value: number, million: string): string {
  return value >= 1_000_000 ? `${(value / 1_000_000).toFixed(2)} ${million}` : `${value.toLocaleString()} ₮`
}

function buildPieOptions(byPie: IByPie[], seriesName: string, centerText: string) {
  return {
    chart: {
      type: "pie" as const,
      height: 200,
      backgroundColor: "transparent",
      style: { fontFamily: "inherit" },
    },
    title: {
      text: centerText,
      align: "center" as const,
      verticalAlign: "middle" as const,
      y: 8,
      style: { fontSize: "14px", color: "var(--mantine-color-gray-8)", textAlign: "center" },
    },
    tooltip: {
      pointFormat: "<b>{point.y:,.0f}₮</b> ({point.percentage:.0f}%)",
      backgroundColor: "var(--mantine-color-white)",
      borderColor: "var(--mantine-color-gray-3)",
      borderRadius: 8,
      style: { fontSize: "14px" },
    },
    plotOptions: {
      pie: {
        innerSize: "72%",
        borderWidth: 0,
        dataLabels: { enabled: false },
        showInLegend: false,
        states: { hover: { brightness: -0.05 } },
      },
    },
    series: [{
      name: seriesName,
      type: "pie" as const,
      data: byPie.map((i) => ({ name: i?.name, y: i?.amount, color: i?.color })),
    }],
    accessibility: { enabled: false },
    credits: { enabled: false },
  }
}
